import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, fetchSignInMethodsForEmail } from 'firebase/auth';
import { getDoc, doc } from 'firebase/firestore';
import './styles/AcceptInvitation.css';
import logo from '../images/logo1.png';

const AcceptInvitation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [invitationData, setInvitationData] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [hasAccount, setHasAccount] = useState(false);
  const [processingAccept, setProcessingAccept] = useState(false);

  const toggleAccountMode = () => {
    setHasAccount(!hasAccount);
    setError(''); // Clear errors on switch
  };

  useEffect(() => {
    // Get invitation data from URL parameters
    const searchParams = new URLSearchParams(location.search);
    const urlToken = searchParams.get('token');
    const invitationId = searchParams.get('invitationId');
    const organizationId = searchParams.get('orgId');
    const email = searchParams.get('email');

    if (!invitationId || !organizationId || !email) {
      setError('Invalid invitation link. Please check that you clicked on the correct link.');
      setLoading(false);
      return;
    }

    // Set email from URL parameter
    setEmail(email);

    // Check if the invitation is valid
    const verifyInvitation = async () => {
      try {
        // Check if the invitation exists and is valid
        const invitationRef = doc(db, 'organizations', organizationId, 'invitations', invitationId);
        const invitationDoc = await getDoc(invitationRef);

        if (!invitationDoc.exists()) {
          throw new Error('The invitation was not found');
        }

        const invitation = invitationDoc.data();
        
        if (invitation.status === 'accepted') {
          throw new Error('This invitation has already been accepted');
        }
        
        if (invitation.status === 'cancelled') {
          throw new Error('This invitation has been cancelled');
        }

        // If token is undefined in URL, use the token from the document
        const token = urlToken || invitation.token;
        
        if (!token) {
          throw new Error('No token found for the invitation');
        }

        if (invitation.email !== email) {
          throw new Error('Invalid invitation. Email does not match.');
        }

        // Get organization data
        const orgRef = doc(db, 'organizations', organizationId);
        const orgDoc = await getDoc(orgRef);
        
        if (!orgDoc.exists()) {
          throw new Error('The organization no longer exists');
        }

        const organization = orgDoc.data();

        // Save invitation data with the correct token
        setInvitationData({
          token,
          invitationId,
          orgId: organizationId,
          email,
          organizationName: organization.name || invitation.organizationName || 'Organization'
        });

        // Check if user already has an account with this email
        try {
          console.log(`AcceptInvitation: Checking if ${email} already has an account`);
          
          // Use fetchSignInMethodsForEmail directly (not through auth object)
          const methods = await fetchSignInMethodsForEmail(auth, email);
          const accountExists = methods.length > 0;
          
          console.log(`AcceptInvitation: User ${email} has ${accountExists ? 'an existing account' : 'no account'}`);
          setHasAccount(accountExists);
        } catch (authErr) {
          console.error('AcceptInvitation: Error checking existing account:', authErr);
          // In case of error, assume user doesn't have an account
          setHasAccount(false);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error verifying invitation:', error);
        setError(error.message || 'An error occurred while verifying the invitation');
        setLoading(false);
      }
    };

    verifyInvitation();
  }, [location.search]);

  const handleAcceptInvitation = async (e) => {
    e.preventDefault();
    setError('');
    setProcessingAccept(true);
    console.log("AcceptInvitation: Starting invitation accept process");

    try {
      if (!invitationData) {
        throw new Error('No invitation data found');
      }

      // If user doesn't have an account, validate passwords
      if (!hasAccount) {
        // Validate password
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setProcessingAccept(false);
          return;
        }

        if (password.length < 6) {
          setError('Password must be at least 6 characters');
          setProcessingAccept(false);
          return;
        }
        
        console.log('AcceptInvitation: New user - backend will create the user');
        // We do NOT create the user here, we let the backend do it
      } else {
        // Login existing user
        try {
          console.log('AcceptInvitation: Existing user - logging in first');
          await signInWithEmailAndPassword(auth, email, password);
          console.log('AcceptInvitation: Login successful');
        } catch (authError) {
          console.error('AcceptInvitation: Error during login:', authError.code, authError.message);
          setError('Incorrect password. Please try again.');
          setProcessingAccept(false);
          return;
        }
      }

      // Verify that all necessary data is present
      if (!invitationData.token) {
        console.error('AcceptInvitation: Missing token');
        throw new Error('Invitation token is missing. Try reloading the page or request a new invitation.');
      }
      
      if (!invitationData.invitationId) {
        console.error('AcceptInvitation: Missing invitationId');
        throw new Error('Invitation ID is missing. Try reloading the page or request a new invitation.');
      }
      
      if (!invitationData.orgId) {
        console.error('AcceptInvitation: Missing orgId');
        throw new Error('Organization ID is missing. Try reloading the page or request a new invitation.');
      }
      
      // Set up data in exactly the structure the backend expects
      const callData = {
        token: invitationData.token,
        invitationId: invitationData.invitationId,
        orgId: invitationData.orgId,
        email: email,
        password: password,
        name: auth.currentUser?.displayName || email.split('@')[0]
      };
      
      // Validate and log content
      console.log('AcceptInvitation: Validation of required fields:');
      console.log('- token:', callData.token ? `OK (${callData.token})` : 'MISSING');
      console.log('- invitationId:', callData.invitationId ? `OK (${callData.invitationId})` : 'MISSING');
      console.log('- orgId:', callData.orgId ? `OK (${callData.orgId})` : 'MISSING');
      console.log('- email:', callData.email ? `OK (${callData.email})` : 'MISSING');
      console.log('- password length:', password ? password.length : 'MISSING');
      console.log('- name:', callData.name || 'MISSING');
      
      // For safety, convert all values to strings to avoid type mismatches
      const sanitizedCallData = {
        token: String(callData.token || ''),
        invitationId: String(callData.invitationId || ''),
        orgId: String(callData.orgId || ''),
        email: String(callData.email || ''),
        password: String(callData.password || ''),
        name: String(callData.name || '')
      };
      
      console.log('AcceptInvitation: Calling Cloud Function with data:', {
        ...sanitizedCallData,
        password: '********' // Don't show password in logs
      });
      
      // Change function name to the correct format with hyphen
      const acceptInvitation = httpsCallable(functions, 'organization-acceptInvitation');
      const result = await acceptInvitation(sanitizedCallData);

      console.log('AcceptInvitation: Cloud Function result:', result.data);
      
      if (result.data.success) {
        // No need to show success screen
        console.log('AcceptInvitation: Invitation accepted successfully!');
        
        // If it was a new user, we try to log in with the new credentials
        if (!hasAccount) {
          try {
            console.log('AcceptInvitation: Attempting to log in with new credentials');
            await signInWithEmailAndPassword(auth, email, password);
            console.log('AcceptInvitation: Auto-login after user creation successful');
          } catch (loginError) {
            console.error('AcceptInvitation: Auto-login failed:', loginError.code, loginError.message);
          }
        }
        
        // Redirect immediately with window.location.href for full reload
        window.location.href = '/organization';
      } else {
        throw new Error(result.data.message || 'An error occurred while accepting the invitation');
      }
    } catch (error) {
      console.error('AcceptInvitation: Error accepting invitation:', error);
      if (error.code) {
        console.error('AcceptInvitation: Error code:', error.code);
      }
      setError(error.message || 'An error occurred while accepting the invitation');
    } finally {
      setProcessingAccept(false);
    }
  };

  // If page is still loading
  if (loading) {
    return (
      <div className="invitation-container">
        <div className="invitation-card">
          <img src={logo} alt="Nuvik.io Logo" className="invitation-logo" />
          <div className="invitation-loading">
            <div className="invitation-spinner"></div>
            <p>Verifying invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  // If there is an error
  if (error && !invitationData) {
    return (
      <div className="invitation-container">
        <div className="invitation-card">
          <img src={logo} alt="Nuvik.io Logo" className="invitation-logo" />
          <div className="invitation-error">
            <i className="fas fa-exclamation-circle"></i>
            <h2>An error occurred</h2>
            <p>{error}</p>
            <button className="invitation-button" onClick={() => navigate('/')}>
              Go to home page
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Normal view - user needs to accept the invitation
  return (
    <div className="invitation-container">
      <div className="invitation-card">
        <img src={logo} alt="Nuvik.io Logo" className="invitation-logo" />
        
        <h1>Invitation to {invitationData?.organizationName}</h1>
        <p className="invitation-description">
          You have been invited to join {invitationData?.organizationName} on Nuvik.io.
        </p>
        
        {error && <div className="invitation-error-message">{error}</div>}
        
        <form onSubmit={handleAcceptInvitation} className="invitation-form">
          <div className="invitation-form-group">
            <label>Email</label>
            <input
              type="email"
              readOnly
              value={email}
              className="invitation-input disabled"
            />
          </div>
          
          <div className="invitation-form-group">
            <label>Password{!hasAccount && ' (choose a password)'}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={hasAccount ? "Your password" : "Choose a secure password"}
              className="invitation-input"
              required
            />
          </div>
          
          {!hasAccount && (
            <div className="invitation-form-group">
              <label>Confirm password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                className="invitation-input"
                required
              />
            </div>
          )}
          
          <button 
            type="submit" 
            className="invitation-button"
            disabled={processingAccept}
          >
            {processingAccept ? (
              <>
                <span className="invitation-spinner small"></span>
                Processing...
              </>
            ) : hasAccount ? (
              'Log in and accept invitation'
            ) : (
              'Create account and accept invitation'
            )}
          </button>
        </form>
        
        <div className="invitation-footer">
          <p>
            {hasAccount ? (
              <>
                Don't have an account? 
                <button 
                  type="button" 
                  className="toggle-account-button"
                  onClick={toggleAccountMode}
                >
                  Create account
                </button>
              </>
            ) : (
              <>
                Already have an account? 
                <button 
                  type="button" 
                  className="toggle-account-button"
                  onClick={toggleAccountMode}
                >
                  Log in
                </button>
              </>
            )}
          </p>
        </div>

        {success && (
          <div className="invitation-success">
            <i className="fas fa-check-circle"></i>
            <h2>Invitation accepted!</h2>
            <p>
              You are now a member of <strong>{invitationData.organizationName}</strong>.
              You will be automatically redirected...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvitation; 